/**
 * Raise Alert feature — small presentational pieces shared by both critical-alert modals.
 */
import React from "react";
import { Icon } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { getPriorityMeta } from "./criticalAlertConstants";

/** Coloured pill showing CRITICAL / URGENT / HIGH CONCERN. */
export const PriorityChip = ({ priority }) => {
  const meta = getPriorityMeta(priority);
  return (
    <span
      className="ca-chip"
      style={{ color: meta.color, background: meta.softBg }}
    >
      {meta.label}
    </span>
  );
};

/** Outlined pill used for "Added manually" / "Auto-detected". */
export const MutedChip = ({ text, color }) => (
  <span className="ca-chip-muted" style={{ color, borderColor: color }}>
    {text}
  </span>
);

/**
 * The sentence the term was found in, with the matched term emphasised so the
 * radiologist can confirm at a glance that it is a real positive finding.
 */
export const EvidenceText = ({ sentence, matchedTerm, accentColor, sources }) => {
  if (!sentence) {
    return null;
  }

  let content = sentence;

  if (matchedTerm) {
    const index = sentence.toLowerCase().indexOf(matchedTerm.toLowerCase());
    if (index !== -1) {
      content = (
        <>
          {sentence.slice(0, index)}
          <mark style={{ color: accentColor }}>
            {sentence.slice(index, index + matchedTerm.length)}
          </mark>
          {sentence.slice(index + matchedTerm.length)}
        </>
      );
    }
  }

  return (
    <p
      className="ca-evidence ca-evidence-quote"
      style={{ borderLeftColor: accentColor }}
    >
      {content}
      {/* Which report template the sentence was written in. Only present when
          the study has more than one, so a single-template report is not
          cluttered with the same label on every line. */}
      {Array.isArray(sources) && sources.length > 0 && (
        <span className="ca-evidence-source">{sources.join(", ")}</span>
      )}
    </p>
  );
};

/**
 * Every sentence that produced this finding, one quote per line.
 *
 * A report can argue the same finding in several places -- "ptx is found."
 * followed later by "PTX is seen." is one pneumothorax, so it stays one card,
 * but the radiologist must be able to see all the lines it came from. Showing
 * only the first made it look as though the engine had missed the others.
 *
 * `occurrences` comes from the engine. Falls back to the single sentence/term
 * pair for findings that predate it, and for manually added findings, which
 * have no matched sentence at all.
 *
 * `collapsed` shows only the representative line with a disclosure for the rest.
 * The confirm modal leaves it off -- the radiologist is deciding there and must
 * see everything without a click. The studylist modal turns it on, because that
 * is a review of a decision already taken and eight findings have to stay
 * scannable.
 */
export const EvidenceList = ({
  occurrences,
  sentence,
  matchedTerm,
  accentColor,
  collapsed = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);

  const list =
    Array.isArray(occurrences) && occurrences.length > 0
      ? occurrences
      : sentence
      ? [{ matchedSentence: sentence, matchedTerm }]
      : [];

  if (list.length === 0) {
    return null;
  }

  /* The representative pair leads.
   *
   * It is `sentence`/`matchedTerm` -- the pair stored on study_critical_alert
   * itself, and the only evidence that existed before the occurrences column.
   * It is NOT reliably occurrences[0]: the engine hands the representative
   * fields to a more severe instance when a per-term priority override outranks
   * the first match, so the most serious sentence can sit anywhere in the array.
   * Collapsing to occurrences[0] would therefore sometimes hide the very line
   * that set the finding's priority. */
  const ordered = React.useMemo(() => {
    if (!sentence || list.length < 2) {
      return list;
    }
    const at = list.findIndex((o) => o.matchedSentence === sentence);
    return at <= 0 ? list : [list[at], ...list.filter((_, i) => i !== at)];
  }, [list, sentence]);

  const hidden = collapsed && !expanded ? ordered.length - 1 : 0;
  const visible = hidden > 0 ? ordered.slice(0, 1) : ordered;

  return (
    <>
      {visible.map((occ, i) => (
        <EvidenceText
          key={`${occ.matchedSentence}-${i}`}
          sentence={occ.matchedSentence}
          matchedTerm={occ.matchedTerm}
          sources={occ.sources}
          accentColor={accentColor}
        />
      ))}

      {collapsed && ordered.length > 1 && (
        <button
          type="button"
          className="ca-evidence-toggle"
          style={{ color: accentColor }}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {/* Fluent icon rather than a bare chevron character: the meta line
              below already uses Icon, and a literal glyph renders at a
              different weight and baseline depending on the installed font. */}
          <Icon
            iconName={expanded ? "ChevronUp" : "ChevronDown"}
            style={{ fontSize: 10, marginRight: 4 }}
          />
          {/* `n`, not `count`: i18next treats a `count` option as a plural
              selector and goes hunting for _one/_other keys. This only renders
              when there is more than one occurrence, so "more" is always right. */}
          {expanded
            ? t("critical-alert.show-less", "Show less")
            : t("critical-alert.show-more", "Show {{n}} more", { n: hidden })}
        </button>
      )}
    </>
  );
};